import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { IconAnalyticsDashboard, IconHumanInWindow, IconLibrary } from '~/ui/core/icons';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';
import type { SideNavigationScope, SideNavigationShortcut } from '~/ui/domain/side-navigation';
import {
    ScopeSwitch,
    SideNavigationChildRow,
    SideNavigationGroup,
    SideNavigationRow,
} from '~/ui/domain/side-navigation';

import { useNavigationMenu } from './NavigationMenuContext';
import type { NavigationMoreEntry } from './NavigationMoreMenu';
import { NavigationMoreMenu } from './NavigationMoreMenu';
import type { NavigationItem, WorkspaceSection } from './navigation-items';
import { groupLandingKeys, isNavigationItemActive, navigationItemKey, orderSectionItems } from './navigation-items';
import { readStored, writeStored } from './navigation-storage';
import { MORE_ENTRY_ATTRIBUTE, useNavigationOverflow } from './use-navigation-overflow';
import { useAreNavigationShortcutsEnabled } from './use-navigation-shortcuts';
import { useOpenGroup } from './use-open-group';

type CompanySection = Exclude<WorkspaceSection, 'footer' | 'me' | 'team'>;

type CompanyGroupHeader = {
    readonly Icon: NavigationItem['Icon'];
    readonly labelKey: TranslationKey;
};

// The Company scope in menu order. A section with a group folds its entries
// under that header; one without lists its entries as top-level rows.
const companySections: readonly {
    readonly group?: CompanyGroupHeader;
    readonly section: CompanySection;
}[] = [
    { section: 'insights' },
    { group: { Icon: IconLibrary, labelKey: 'navigation.group.architecture' }, section: 'architecture' },
    { section: 'transformation' },
    { group: { Icon: IconAnalyticsDashboard, labelKey: 'navigation.group.talent' }, section: 'talent' },
    { group: { Icon: IconHumanInWindow, labelKey: 'navigation.group.people' }, section: 'people' },
];

const groupSections: readonly CompanySection[] = companySections.flatMap((entry) =>
    entry.group === undefined ? [] : [entry.section],
);

// The entry the current page belongs to and the group holding it, which the rail opens.
const activePlace = (
    item: NavigationItem | undefined,
): { readonly activeEntry: string | null; readonly activeGroup: CompanySection | null } => ({
    activeEntry: item === undefined ? null : navigationItemKey(item),
    activeGroup: groupSections.find((section) => section === item?.workspaceSection) ?? null,
});

// A rail row's height (navRowClassName), the room the "…" entry takes.
const NAV_ROW_HEIGHT_PX = 34;

// Pages under different route roots remount the rail, so the selected scope and
// the last page per scope live in session storage rather than component state.
const SCOPE_MEMORY_STORAGE_KEY = 'workspace-navigation-scope-memory';

const scopeMemorySchema = z
    .object({
        paths: z
            .object({
                company: z.string().optional().catch(undefined),
                me: z.string().optional().catch(undefined),
                team: z.string().optional().catch(undefined),
            })
            .catch({}),
        scope: z.enum(['company', 'me', 'team']).catch('me'),
    })
    .catch({ paths: {}, scope: 'me' });

type ScopeMemory = z.infer<typeof scopeMemorySchema>;

const readScopeMemory = (): ScopeMemory =>
    scopeMemorySchema.parse(readStored('sessionStorage', SCOPE_MEMORY_STORAGE_KEY));

const rememberScope = (memory: ScopeMemory): ScopeMemory => {
    writeStored('sessionStorage', SCOPE_MEMORY_STORAGE_KEY, memory);

    return memory;
};

// Scope selection matches by prefix on purpose: a page below an entry belongs to that entry's scope.
const isPathActive = (pathname: string, path: string): boolean => pathname === path || pathname.startsWith(`${path}/`);

const scopeOfSection = (section: WorkspaceSection): SideNavigationScope | null => {
    if (section === 'footer') {
        return null;
    }

    return section === 'me' || section === 'team' ? section : 'company';
};

const useItemShortcut = (item: NavigationItem | undefined): SideNavigationShortcut | undefined => {
    const translate = useTranslate();
    const areShortcutsEnabled = useAreNavigationShortcutsEnabled();
    const shortcutKey = areShortcutsEnabled ? item?.shortcutKey : undefined;

    return item === undefined || shortcutKey === undefined
        ? undefined
        : {
              keys: ['G', shortcutKey.toUpperCase()],
              label: translate('navigation.shortcut-sequence', { key: shortcutKey.toUpperCase() }),
          };
};

const NavRow: FunctionComponent<{ readonly expanded: boolean; readonly item: NavigationItem }> = ({
    expanded,
    item,
}) => {
    const { actions } = useApplicationContext();
    const { pathname } = useLocation();
    const shortcut = useItemShortcut(item);

    if (item.externalUrl) {
        return (
            <SideNavigationRow
                expanded={expanded}
                Icon={item.Icon}
                IconAdornment={item.IconAdornment}
                label={item.menuLabel}
                shortcut={shortcut}
                onClick={() => actions.openWindow(item.path)}
            />
        );
    }

    return (
        <SideNavigationRow
            active={isNavigationItemActive(pathname, item)}
            as={Link}
            expanded={expanded}
            Icon={item.Icon}
            IconAdornment={item.IconAdornment}
            label={item.menuLabel}
            shortcut={shortcut}
            to={item.path}
        />
    );
};

// Where a picked scope's entries come in from: the right for a wider scope
// (Me, then Company), the left for a narrower one.
type ScopeEntrance = { readonly from: 'narrower' | 'wider'; readonly scope: SideNavigationScope };

// Picking a scope navigates, and the navigation mounts afresh on the new page,
// so the pick waits here for the first navigation that shows its scope.
let pendingScopeEntrance: ScopeEntrance | null = null;

const CompanyGroup: FunctionComponent<{
    readonly expanded: boolean;
    readonly folded: boolean;
    readonly header: CompanyGroupHeader;
    readonly hidden: boolean;
    readonly items: readonly NavigationItem[];
    readonly landing: NavigationItem | undefined;
    readonly onToggle: () => void;
}> = ({ expanded, folded, header, hidden, items, landing, onToggle }) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const landingShortcut = useItemShortcut(landing);
    const rows = items.filter((item) => item !== landing);
    const label = translate(header.labelKey);

    if (landing !== undefined && rows.length === 0) {
        return (
            <li hidden={hidden}>
                <NavRow expanded={expanded} item={{ ...landing, Icon: header.Icon, menuLabel: label }} />
            </li>
        );
    }

    const isRowActive = rows.some((item) => isNavigationItemActive(pathname, item));
    const firstPath = rows[0]?.path;
    const rowItems = rows.map((item) => (
        <li key={navigationItemKey(item)}>
            <SideNavigationChildRow
                active={isNavigationItemActive(pathname, item)}
                as={Link}
                expanded={expanded}
                label={item.menuLabel}
                to={item.path}
            />
        </li>
    ));

    if (landing !== undefined) {
        return (
            <SideNavigationGroup
                active={isNavigationItemActive(pathname, landing) || ((folded || !expanded) && isRowActive)}
                as={Link}
                current={isNavigationItemActive(pathname, landing)}
                expanded={expanded}
                folded={folded}
                hidden={hidden}
                Icon={header.Icon}
                IconAdornment={landing.IconAdornment}
                label={label}
                shortcut={landingShortcut}
                to={landing.path}
                toggle={{ label: translate(folded ? 'common.expand' : 'common.collapse'), onToggle }}
                onHeaderClick={() => {
                    if (expanded && folded) {
                        onToggle();
                    }
                }}
            >
                {rowItems}
            </SideNavigationGroup>
        );
    }

    return (
        <SideNavigationGroup
            // Pressed when its current page has no row of its own to show it: folded away,
            // or only a stripe on the folded rail.
            active={(folded || !expanded) && isRowActive}
            expanded={expanded}
            folded={folded}
            hidden={hidden}
            Icon={header.Icon}
            label={label}
            onHeaderClick={() => {
                if (expanded || firstPath === undefined) {
                    onToggle();
                } else {
                    navigate(firstPath);
                }
            }}
        >
            {rowItems}
        </SideNavigationGroup>
    );
};

// One entry of the rail as the overflow sees it: shown in place, or set aside whole behind "…".
type RailUnit = {
    readonly more: NavigationMoreEntry;
    readonly render: (hidden: boolean) => ReactNode;
};

const rowUnit = (item: NavigationItem, expanded: boolean): RailUnit => ({
    more: { item, kind: 'row' },
    render: (hidden) => (
        <li key={navigationItemKey(item)} hidden={hidden}>
            <NavRow expanded={expanded} item={item} />
        </li>
    ),
});

const groupUnit = ({
    expanded,
    folded,
    header,
    items,
    label,
    landing,
    onToggle,
    section,
}: {
    readonly expanded: boolean;
    readonly folded: boolean;
    readonly header: CompanyGroupHeader;
    readonly items: readonly NavigationItem[];
    readonly label: string;
    readonly landing: NavigationItem | undefined;
    readonly onToggle: () => void;
    readonly section: CompanySection;
}): RailUnit => {
    const rows = items.filter((item) => item !== landing);

    return {
        more:
            landing !== undefined && rows.length === 0
                ? { item: { ...landing, Icon: header.Icon, menuLabel: label }, kind: 'row' }
                : { Icon: header.Icon, items: rows, key: section, kind: 'group', label, landing },
        render: (hidden) => (
            <CompanyGroup
                key={section}
                expanded={expanded}
                folded={folded}
                header={header}
                hidden={hidden}
                items={items}
                landing={landing}
                onToggle={onToggle}
            />
        ),
    };
};

// The scope's entries over the footer. The rail never scrolls: what does not fit
// moves behind a "…" entry at the end of the entries, the footer's links first,
// then the entries from the bottom up, a group always whole.
const RailEntries: FunctionComponent<{
    readonly entrance: ScopeEntrance['from'] | null;
    readonly expanded: boolean;
    readonly footer: ReactNode;
    readonly footerItems: readonly NavigationItem[];
    readonly scope: SideNavigationScope;
    readonly units: readonly RailUnit[];
}> = ({ entrance, expanded, footer, footerItems, scope, units }) => {
    const container = useRef<HTMLDivElement>(null);
    const entries = useRef<HTMLDivElement>(null);
    const links = useRef<HTMLUListElement>(null);
    const tail = useRef<HTMLDivElement>(null);
    const overflow = useNavigationOverflow({ container, entries, links, tail }, NAV_ROW_HEIGHT_PX);
    const hidden = new Set(overflow.hidden);
    const hasMore = hidden.size > 0 || overflow.linksHidden;

    return (
        <div ref={container} className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div ref={entries}>
                <div
                    key={scope}
                    className={entrance === null ? undefined : 'side-nav-scope-enter'}
                    data-scope-entrance={entrance ?? undefined}
                >
                    <ul className="m-0 flex list-none flex-col gap-xxs p-0">
                        {units.map((unit, index) => unit.render(hidden.has(index)))}
                        {hasMore ? (
                            <li {...{ [MORE_ENTRY_ATTRIBUTE]: '' }}>
                                <NavigationMoreMenu
                                    entries={units.filter((_, index) => hidden.has(index)).map(({ more }) => more)}
                                    expanded={expanded}
                                    links={overflow.linksHidden ? footerItems : []}
                                />
                            </li>
                        ) : null}
                    </ul>
                </div>
            </div>
            <div ref={tail} className="mt-auto flex shrink-0 flex-col gap-xxs pt-xl">
                <ul
                    ref={links}
                    className="m-0 flex list-none flex-col gap-xxs p-0 [&[hidden]]:hidden"
                    hidden={overflow.linksHidden}
                >
                    {footerItems.map((item) => (
                        <li key={navigationItemKey(item)}>
                            <NavRow expanded={expanded} item={item} />
                        </li>
                    ))}
                </ul>
                {footer}
            </div>
        </div>
    );
};

// The flagged side navigation: a Me / Teams / Company scope switch over the
// scope's entries, with the footer entries shared by every scope. Teams and
// Company appear as soon as one of their entries passes its own check. The account
// menu stays pinned below.
export const WorkspaceNavigation: FunctionComponent<{
    readonly footer: ReactNode;
    readonly items: readonly NavigationItem[];
}> = ({ footer, items }) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const { isExpanded: expanded } = useNavigationMenu();
    const [scopeMemory, setScopeMemory] = useState<ScopeMemory>(readScopeMemory);
    const selectedScope = scopeMemory.scope;

    const meItems = items.filter((item) => item.workspaceSection === 'me');
    const teamItems = items.filter((item) => item.workspaceSection === 'team');
    const footerItems = items.filter((item) => item.workspaceSection === 'footer');
    const sections = companySections.flatMap((entry) => {
        const sectionItems = orderSectionItems(
            entry.section,
            items.filter((item) => item.workspaceSection === entry.section),
        );
        const landing = sectionItems.find((item) => item.labelKey === groupLandingKeys[entry.section]);

        return sectionItems.length === 0 ? [] : [{ ...entry, items: sectionItems, landing }];
    });

    const scopes: readonly SideNavigationScope[] = [
        ...(meItems.length > 0 ? (['me'] as const) : []),
        ...(teamItems.length > 0 ? (['team'] as const) : []),
        ...(sections.length > 0 ? (['company'] as const) : []),
    ];

    const matchedItem = items
        .filter((item) => !item.externalUrl && isPathActive(pathname, item.path))
        .sort((left, right) => right.path.length - left.path.length)[0];
    const matchedScope = matchedItem === undefined ? null : scopeOfSection(matchedItem.workspaceSection);
    const { openGroup, toggleGroup } = useOpenGroup({ ...activePlace(matchedItem), groups: groupSections });
    const scope: SideNavigationScope =
        matchedScope ?? (scopes.includes(selectedScope) ? selectedScope : (scopes[0] ?? 'me'));
    // Only a pick in the switch slides the entries in; the scope settling on load
    // or following a link stays put.
    const [pickedEntrance, setPickedEntrance] = useState<ScopeEntrance | null>(() => pendingScopeEntrance);

    if (
        pendingScopeEntrance !== null &&
        pendingScopeEntrance !== pickedEntrance &&
        pendingScopeEntrance.scope === scope
    ) {
        setPickedEntrance(pendingScopeEntrance);
    }

    const entrance = pickedEntrance?.scope === scope ? pickedEntrance.from : null;

    useEffect(() => {
        if (pendingScopeEntrance?.scope === scope) {
            pendingScopeEntrance = null;
        }
    }, [scope]);

    useEffect(() => {
        if (matchedScope !== null) {
            setScopeMemory((current) =>
                rememberScope({
                    paths: { ...current.paths, [matchedScope]: `${pathname}${search}` },
                    scope: matchedScope,
                }),
            );
        }
    }, [matchedScope, pathname, search]);

    const selectScope = (value: SideNavigationScope): void => {
        if (value !== scope) {
            pendingScopeEntrance = {
                from: scopes.indexOf(value) < scopes.indexOf(scope) ? 'narrower' : 'wider',
                scope: value,
            };
        }
        setScopeMemory((current) => rememberScope({ ...current, scope: value }));
        const scopeItems = {
            company: sections.flatMap((entry) => entry.items),
            me: meItems,
            team: teamItems,
        }[value];
        const remembered = scopeMemory.paths[value];
        // The remembered page can disappear when permissions change mid-session.
        const destination =
            remembered !== undefined &&
            scopeItems.some((item) => isPathActive(remembered.split('?')[0] ?? '', item.path))
                ? remembered
                : scopeItems[0]?.path;

        if (destination !== undefined) {
            navigate(destination);
        }
    };

    return (
        <Tooltip.Provider>
            <div className="flex h-full min-h-0 flex-1 flex-col">
                {scopes.length > 1 ? (
                    <ScopeSwitch
                        ariaLabel={translate('navigation.scope.label')}
                        current={scope}
                        expanded={expanded}
                        options={scopes.map((value) => ({
                            label: translate(`navigation.scope.${value}`),
                            scope: value,
                        }))}
                        onSelect={selectScope}
                    />
                ) : null}
                <RailEntries
                    entrance={entrance}
                    expanded={expanded}
                    footer={footer}
                    footerItems={footerItems}
                    scope={scope}
                    units={
                        scope === 'company'
                            ? sections.flatMap(({ group, items: sectionItems, landing, section }) =>
                                  group === undefined
                                      ? sectionItems.map((item) => rowUnit(item, expanded))
                                      : [
                                            groupUnit({
                                                expanded,
                                                folded: openGroup !== section,
                                                header: group,
                                                items: sectionItems,
                                                label: translate(group.labelKey),
                                                landing,
                                                onToggle: () => toggleGroup(section),
                                                section,
                                            }),
                                        ],
                              )
                            : (scope === 'me' ? meItems : teamItems).map((item) => rowUnit(item, expanded))
                    }
                />
            </div>
        </Tooltip.Provider>
    );
};
